<template>
  <nav class="atlas-ingestion-stepper" aria-label="Bulk upload progress">
    <ol class="atlas-ingestion-stepper__list">
      <li v-for="(step, index) in props.steps" :key="step.id" class="atlas-ingestion-stepper__item">
        <div class="atlas-ingestion-stepper__circle-wrap">
          <span
            class="atlas-ingestion-stepper__circle"
            :class="{
              'atlas-ingestion-stepper__circle--complete': step.id < props.currentStep,
              'atlas-ingestion-stepper__circle--current': step.id === props.currentStep,
              'atlas-ingestion-stepper__circle--upcoming': step.id > props.currentStep,
            }"
            :aria-current="step.id === props.currentStep ? 'step' : undefined"
          >
            <IconEmbedded
              v-if="step.id < props.currentStep"
              name="check_2"
              :size="14"
              color="#fff"
              class="atlas-ingestion-stepper__check"
            />
            <span v-else class="atlas-ingestion-stepper__num">{{ step.id }}</span>
          </span>
          <span
            class="atlas-ingestion-stepper__label"
            :class="{
              'atlas-ingestion-stepper__label--active': step.id === props.currentStep,
              'atlas-ingestion-stepper__label--muted': step.id !== props.currentStep,
            }"
          >
            {{ step.label }}
          </span>
        </div>
        <div
          v-if="index < props.steps.length - 1"
          class="atlas-ingestion-stepper__connector"
          :class="{
            'atlas-ingestion-stepper__connector--complete': props.currentStep > step.id,
          }"
          role="presentation"
          aria-hidden="true"
        />
      </li>
    </ol>
  </nav>
</template>

<script setup lang="ts">
import IconEmbedded from "@components/ui/IconEmbedded.vue";

export interface AtlasIngestionStepDef {
  id: number;
  label: string;
}

defineOptions({
  name: "AtlasIngestionStepper",
});

const props = defineProps<{
  currentStep: number;
  steps: Array<AtlasIngestionStepDef>;
}>();
</script>

<style scoped lang="scss">
$teal: #118689;
$muted: #9a9a9a;
$line: #d8d8d8;

.atlas-ingestion-stepper {
  width: 100%;
  max-width: 100%;
  margin-bottom: 36px;
  box-sizing: border-box;
}

.atlas-ingestion-stepper__list {
  list-style: none;
  margin: 0;
  padding: 0;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  flex-wrap: nowrap;
  gap: 0;
}

.atlas-ingestion-stepper__item {
  flex: 1 1 0;
  display: flex;
  align-items: center;
  min-width: 0;

  &:last-child {
    flex: 0 0 auto;
  }
}

.atlas-ingestion-stepper__circle-wrap {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}

.atlas-ingestion-stepper__circle {
  width: 32px;
  height: 32px;
  min-width: 32px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font:
    700 14px/1 "Quicksand",
    sans-serif;
  box-sizing: border-box;
  transition:
    background 0.15s ease,
    border-color 0.15s ease,
    color 0.15s ease;

  &--complete {
    background: $teal;
    border: 2px solid $teal;
    color: #fff;
  }

  &--current {
    background: $teal;
    border: 2px solid $teal;
    color: #fff;
  }

  &--upcoming {
    background: #fff;
    border: 2px solid $line;
    color: $muted;
  }
}

.atlas-ingestion-stepper__num {
  margin-top: 1px;
}

.atlas-ingestion-stepper__check {
  display: flex;
}

.atlas-ingestion-stepper__label {
  font:
    600 15px/1.2 "Quicksand",
    sans-serif;
  white-space: nowrap;

  &--active {
    color: #1a1a1a;
    font-weight: 700;
  }

  &--muted {
    color: $muted;
    font-weight: 600;
  }
}

.atlas-ingestion-stepper__connector {
  flex: 1 1 auto;
  min-width: 8px;
  height: 2px;
  background: $line;
  transition: background 0.15s ease;
  /* After label, before line; after line, before next step’s circle */
  margin: 0 12px 0 10px;
  align-self: center;

  &--complete {
    background: $teal;
  }
}

@media (max-width: 640px) {
  .atlas-ingestion-stepper__circle-wrap {
    gap: 6px;
  }

  .atlas-ingestion-stepper__connector {
    min-width: 4px;
    margin: 0 8px 0 6px;
  }

  .atlas-ingestion-stepper__label {
    font-size: 12px;
  }
}
</style>
